import { ref, listAll, getDownloadURL, getMetadata, type StorageReference } from 'firebase/storage';
import { initializeAuth, storage } from './firebase';

export type CaseStudyImage = {
  image: string;
  fileName: string;
  timeCreated: number;
};

const CASE_STUDIES_DIRECTORY = 'Case-Studies';
const DISPLAY_ORDER_MANIFEST = 'display_order.json';

function applyManifestOrder(images: CaseStudyImage[], manifestOrder: string[]): CaseStudyImage[] {
  const orderedNames = manifestOrder.filter((name) => name && name !== DISPLAY_ORDER_MANIFEST);
  if (orderedNames.length === 0) {
    return images;
  }

  const sorted: CaseStudyImage[] = [];
  const matched = new Set<CaseStudyImage>();

  for (const name of orderedNames) {
    const match = images.find((item) => !matched.has(item) && item.fileName === name);
    if (match) {
      sorted.push(match);
      matched.add(match);
    }
  }

  for (const item of images) {
    if (!matched.has(item)) {
      sorted.push(item);
    }
  }

  return sorted;
}

async function fetchDisplayOrder(directory: string): Promise<string[]> {
  try {
    const manifestRef = ref(storage, `${directory}/${DISPLAY_ORDER_MANIFEST}`);
    const url = await getDownloadURL(manifestRef);
    const response = await fetch(url);
    if (!response.ok) {
      return [];
    }
    const order: string[] = await response.json();
    return order;
  } catch (error: any) {
    if (error?.code !== 'storage/object-not-found') {
      console.error(`Failed to fetch display order for ${directory}:`, error);
    }
    return [];
  }
}

export async function fetchCaseStudyImages(): Promise<CaseStudyImage[]> {
  const user = await initializeAuth();
  if (!user) {
    throw new Error('User authentication failed');
  }
  await user.getIdToken();

  const directoryRef = ref(storage, CASE_STUDIES_DIRECTORY);
  const result = await listAll(directoryRef);

  const imagePromises = result.items
    .filter((itemRef: StorageReference) => {
      return (
        !itemRef.name.startsWith('.') &&
        !itemRef.name.endsWith('.json') &&
        itemRef.name.toLowerCase() !== '.keep' &&
        itemRef.name !== DISPLAY_ORDER_MANIFEST
      );
    })
    .map(async (itemRef: StorageReference) => {
      const [downloadURL, metadata] = await Promise.all([
        getDownloadURL(itemRef),
        getMetadata(itemRef),
      ]);
      return {
        image: downloadURL,
        fileName: itemRef.name,
        timeCreated: metadata.timeCreated ? new Date(metadata.timeCreated).getTime() : 0,
      };
    });

  const images: CaseStudyImage[] = await Promise.all(imagePromises);
  images.sort((a: CaseStudyImage, b: CaseStudyImage) => b.timeCreated - a.timeCreated);

  const manifestSort = await Promise.race([
    fetchDisplayOrder(CASE_STUDIES_DIRECTORY),
    new Promise<string[]>((resolve) => setTimeout(() => resolve([]), 2500)),
  ]);
  return applyManifestOrder(images, manifestSort);
}
